import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CustomButton from "src/components/CustomButton";
import CustomTable from "src/components/CustomTable";
import ClearIcon from "@mui/icons-material/Clear";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { GET_LOCATION_ENUM_ALL } from "src/graphql/query";
import { UPDATE_POPULAR_LOCATION_ENUM_RANK } from "src/graphql/mutations";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Loader from "src/components/Loader";
import ErrorComponent from "src/components/ErrorComponent";
import CreatePopularLocationEnum from "./CreatePopularLocationEnum";
import DeleteModel from "src/components/Common/DeleteModel";
import { toast } from "react-toastify";
import {
  DELETE_POPULAR_LOCATION_ENUM,
  DELETE_MULTIPLE_POPULAR_LOCATION_ENUM,
  DELETE_ALL_POPULAR_LOCATION_ENUM,
} from "src/graphql/DeleteMutation";
import { getRole } from "src/components/Common/Utils";
import ActionMenu from "../Business/ActionMenu";
import { debounce } from "src/utils/debounce";
import SaveIcon from "@mui/icons-material/Save";
import ToggleSwitch from "src/components/Common/ToggleSwitch";

function PopularLocationEnum() {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [total, setTotal] = useState(0);
  const [list, setList] = useState<any>([]);
  const [selectedEventEnum, setSelectedEventEnum] = useState<any>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [rankUpdates, setRankUpdates] = useState<{
    [key: string]: number | null;
  }>({});
  const [isRankEdit, setIsRankEdit] = useState<boolean>(false);
  const [UpdatePopularLocationEnumRank] = useMutation(
    UPDATE_POPULAR_LOCATION_ENUM_RANK 
  );
  const [enumToDelete, setEnumToDelete] = useState<any>(null);
  const [DeletePopularLocationEnum] = useMutation(DELETE_POPULAR_LOCATION_ENUM);
  const [selectedRows, setSelectedRows] = useState([]);
  const [DeleteMultiplePopularLocationEnum] = useMutation(
    DELETE_MULTIPLE_POPULAR_LOCATION_ENUM
  );
  const [DeleteAllPopularLocationEnum] = useMutation(
    DELETE_ALL_POPULAR_LOCATION_ENUM
  );
  const [openDeleteMultipleModel, setOpenDeleteMultipleModel] =
    useState<boolean>(false);
  const [openDeleteAllModel, setOpenDeleteAllModel] = useState<boolean>(false);
  const isSuperAdmin = getRole() === "SuperAdmin";
  const COLUMNS = [
    ...(isSuperAdmin
      ? [
        {
          headerName: (
            <Box display={"flex"} alignItems={"center"}>
              <Checkbox
                checked={
                  selectedRows.length === list.length && list.length > 0
                }
                indeterminate={
                  selectedRows.length > 0 && selectedRows.length < list.length
                }
                onChange={(event) => handleSelectAll(event.target.checked)}
              />
            </Box>
          ),
          field: "select",
          flex: 0.5,
          sortable: false,
          filterable: false,
          disableClickEventBubbling: true,
          renderCell: ({ row }: any) => (
            <Checkbox
              checked={selectedRows?.includes(row?._id as never)}
              onChange={() => handleRowSelect(row._id)}
            />
          ),
          headerClassName: "no-hover",
        },
      ]
      : []),
    { headerName: "Name", field: "name", flex: 1 },
    { headerName: "Description", field: "description", flex: 1 },
    {
      headerName: "Rank",
      field: "rank",
      flex: 1,
      renderCell: ({ row }: { row: any }) => {
        const [rankValue, setRankValue] = useState(row?.rank || "");

        const handleRankChange = (value: string) => {
          setRankValue(value);
          setRankUpdates((prev) => ({
            ...prev,
            [row._id]: value === "" ? null : Number(value),
          }));
        };
        return isRankEdit ? (
          <TextField
            value={rankValue}
            onChange={(e) => handleRankChange(e.target.value)}
            placeholder="Enter Rank"
            variant="outlined"
            size="small"
            style={{ marginRight: "8px" }}
          />
        ) : (
          <Typography
            display="flex"
            alignItems="center"
            style={{ height: "100%" }}
          >
            {rankValue}
          </Typography>
        );
      },
    },
    {
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }: { row: any }) => (
        <Box display={"flex"} alignItems={"center"}>
          <Tooltip title="Edit">
            <IconButton
              onClick={() => handleEdit(row)}
              aria-label="edit"
              style={{ marginRight: "8px", color: "#00C5B9" }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              onClick={() => handleDelete(row)}
              aria-label="delete"
              style={{ color: "#00C5B9" }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];
  const handleRowSelect = (id: any) => {
    setSelectedRows((prevSelectedRows: any) =>
      prevSelectedRows.includes(id)
        ? prevSelectedRows.filter((rowId: any) => rowId !== id)
        : [...prevSelectedRows, id]
    );
  };
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allRowIds = list.map((row: any) => row._id);
      setSelectedRows(allRowIds as any);
    } else {
      setSelectedRows([]);
    }
  };

  const handleMultipleDelete = async () => {
    try {
      const res = await DeleteMultiplePopularLocationEnum({
        variables: {
          _id: selectedRows,
        },
      });
      if (res?.data?.deleteMultiplePopularLocationEnum?.statusCode === 200) {
        toast.success(res?.data?.deleteMultiplePopularLocationEnum?.message);
        refetch();
      } else {
        throw new Error(res?.data?.deleteMultiplePopularLocationEnum?.message);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message);
    } finally {
      setOpenDeleteMultipleModel(false);
    }
  };

  const handleAllDelete = async () => {
    try {
      const res = await DeleteAllPopularLocationEnum();
      if (res?.data?.deleteAllPopularLocationEnum?.statusCode === 200) {
        toast.success(res?.data?.deleteAllPopularLocationEnum?.message);
        refetch();
      } else {
        throw new Error(res?.data?.deleteAllPopularLocationEnum?.message);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message);
    } finally {
      setOpenDeleteAllModel(false);
    }
  };

  const handleEdit = (rowData: any) => {
    setSelectedEventEnum(rowData);
    setOpenModal(true);
  };
  const handleDelete = (rowData: any) => {
    setEnumToDelete(rowData);
    setOpenConfirmDialog(true);
  };
  const { loading, error, data, refetch } = useQuery(
    GET_LOCATION_ENUM_ALL,
    {
      variables: {
        page: pagination.page,
        limit: pagination.pageSize,
        search: searchTerm,
      },
    }
  );
  useEffect(() => {
    if (data?.getAllPopularLocationEnum) {
      setList(data?.getAllPopularLocationEnum?.data);
      setTotal(data?.getAllPopularLocationEnum?.total);
    }
  }, [data, refetch]);

  const debouncedRefetch = useCallback(
    debounce((term: string) => {
      refetch({
        search: term,
        page: 1,
        limit: pagination.pageSize,
      });
    }, 1000),
    [refetch, pagination.pageSize]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    debouncedRefetch(value);
  };
  const handleClearSearch = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch({ search: "", page: 1, limit: pagination.pageSize });
  };
  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };
  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEventEnum(undefined);
  };

  const handleRankUpdate = async () => {
    const input = Object.keys(rankUpdates).map((id) => ({
      popularLocationId: id,
      rank: rankUpdates[id],
    }));
    try {
      for (const { popularLocationId, rank } of input) {
        try {
          const EnumPopularLocationRank = {
            _id: popularLocationId,
            newRank: rank,
          };
          const response = await UpdatePopularLocationEnumRank({
            variables: EnumPopularLocationRank,
          });
          if (
            response?.data?.updatePopularLocationEnumRank?.statusCode === 200
          ) {
            toast.success(response.data.updatePopularLocationEnumRank.message);
            setRankUpdates({});
          } else {
            toast.error(
              response.data.updatePopularLocationEnumRank.message
            );
          }
        } catch (error) {
          toast.error(`${error}`);
        }
      }
    } catch (error: any) {
      toast.error(`Failed to update ranks: ${error.message}`);
    } finally {
      setIsRankEdit(false);
      refetch();
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (enumToDelete) {
        const res = await DeletePopularLocationEnum({
          variables: { _id: enumToDelete._id },
        });
        if (res?.data?.deletePopularLocationEnum?.statusCode === 200) {
          toast.success(res?.data?.deletePopularLocationEnum?.message);
          setEnumToDelete(null);
          setOpenConfirmDialog(false);
          refetch();
        } else {
          throw new Error(res?.data?.deletePopularLocationEnum?.message);
        }
      }
    } catch (error: any) {
      toast.error(
        error?.message || "An error occurred while deleting the user."
      );
    }
  };
  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        paddingRight={4}
      >
        <Typography variant="h4" style={{ color: "#00C5B9" }}>
          Popular Location Enums
        </Typography>

        <Box display="flex" alignItems="center" gap={2}>
          <ToggleSwitch mode={isRankEdit} setMode={setIsRankEdit} />
          <Box width={200}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {isSuperAdmin &&
            <ActionMenu
              isSuperAdmin={isSuperAdmin}
              setOpenDeleteMultipleModel={setOpenDeleteMultipleModel}
              setOpenDeleteAllModel={setOpenDeleteAllModel}
            />
          }
          {isRankEdit && (
            <Tooltip title="Save">
              <Box width={50} mr={2}>
                <CustomButton
                  onClick={handleRankUpdate}
                  variant="contained"
                  sx={{ width: 50 }}
                >
                  <SaveIcon />
                </CustomButton>
              </Box>
            </Tooltip>
          )}
          <Box>
            <CustomButton
              onClick={() => setOpenModal(true)}
              variant="contained"
              className="width: 200px"
            >
              Create
            </CustomButton>
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box mt={2}>
        <CustomTable
          columns={COLUMNS}
          data={list}
          paginationModel={{
            page: pagination.page - 1,
            pageSize: pagination.pageSize,
          }}
          totalCount={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </Box>
      <CreatePopularLocationEnum
        open={openModal}
        handleClose={() => handleCloseModal()}
        refetchDeals={refetch}
        selectedDealEnum={selectedEventEnum}
      />
      <DeleteModel
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        message="Are you sure you want to delete this location enum?"
      />
      {openDeleteMultipleModel && (
        <DeleteModel
          open={openDeleteMultipleModel}
          onClose={() => {
            setOpenDeleteMultipleModel(false);
          }}
          onConfirm={handleMultipleDelete}
          message={`Are you sure you want to delete ${selectedRows?.length ?? 0
            } location enums?`}
        />
      )}
      {openDeleteAllModel && (
        <DeleteModel
          open={openDeleteAllModel}
          onClose={() => {
            setOpenDeleteAllModel(false);
          }}
          onConfirm={handleAllDelete}
          message="Are you sure you want to delete all location enum?"
        />
      )}
    </Box>
  );
}

export default PopularLocationEnum;
